import React, { useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import ApiService from '../../services/api';

function rowCityName(row) {
  return String(row?.name || row?.display_name || '').trim();
}

function rowState(row) {
  return String(row?.state || row?.state_name || row?.stateName || '').trim() || null;
}

/**
 * Searchable city multi-select backed by BFF `GET /locations/search`.
 * Value shape: `{ city, state?, country? }[]`
 */
export default function AdsCitySearchSelect({
  value = [],
  onChange,
  placeholder = 'Search and add cities',
  className = 'w-full',
}) {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const metaByCity = useMemo(() => {
    const map = new Map();
    for (const loc of value) {
      if (loc?.city) map.set(String(loc.city), loc);
    }
    for (const opt of options) {
      if (opt?.value && !map.has(opt.value)) {
        map.set(opt.value, {
          city: opt.value,
          state: opt.state || null,
          country: opt.country || 'IN',
        });
      }
    }
    return map;
  }, [value, options]);

  const selectedNames = useMemo(
    () => (Array.isArray(value) ? value.map((l) => l.city).filter(Boolean) : []),
    [value],
  );

  const displayOptions = useMemo(() => {
    const map = new Map();
    for (const name of selectedNames) {
      map.set(name, { value: name, label: name });
    }
    for (const opt of options) {
      map.set(opt.value, { value: opt.value, label: opt.label });
    }
    return [...map.values()];
  }, [selectedNames, options]);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 1) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await ApiService.bffSearchLocations({
          search: q,
          limit: 30,
          include_localities: false,
        });
        if (cancelled) return;
        const rows = Array.isArray(res?.data) ? res.data : [];
        const next = [];
        const seen = new Set();
        for (const row of rows) {
          // Prefer city rows; skip localities/areas for campaign geo targeting
          if (row?.type && row.type !== 'city') continue;
          const name = rowCityName(row);
          if (!name || seen.has(name)) continue;
          seen.add(name);
          const state = rowState(row);
          next.push({
            value: name,
            label: state ? `${name}, ${state}` : name,
            state,
            country: String(row?.country || row?.country_code || 'IN').trim() || 'IN',
          });
        }
        setOptions(next);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search]);

  return (
    <div>
      <Select
        mode="multiple"
        showSearch
        allowClear
        placeholder={placeholder}
        className={className}
        value={selectedNames}
        options={displayOptions}
        filterOption={false}
        loading={loading}
        onSearch={setSearch}
        onChange={(names) => {
          const next = (names || []).map((city) => {
            const prev = metaByCity.get(city);
            return {
              city,
              state: prev?.state || null,
              country: prev?.country || 'IN',
            };
          });
          onChange(next);
          setSearch('');
        }}
        maxTagCount="responsive"
        listHeight={256}
        notFoundContent={
          loading ? 'Searching…' : search.trim() ? 'No cities found' : 'Type to search cities'
        }
      />
      <p className="mt-1.5 text-xs text-slate-500">
        {selectedNames.length
          ? `${selectedNames.length} ${selectedNames.length === 1 ? 'city' : 'cities'} targeted`
          : 'Leave empty to target all cities'}
      </p>
    </div>
  );
}
